import React from "react";
import { useTranslation } from "react-i18next";
import { CommunicationIntegration } from "@/types/communication-integration";

interface IntegrationSummaryProps {
  integration: CommunicationIntegration;
}

interface Row {
  label: string;
  value: string;
  mono?: boolean;
}

const SummaryRows: React.FC<{ rows: Row[] }> = ({ rows }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
    {rows.map((row) => (
      <div key={row.label} className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          {row.label}
        </span>
        <span
          className={`text-sm text-foreground break-all ${
            row.mono ? "font-mono" : ""
          }`}
        >
          {row.value}
        </span>
      </div>
    ))}
  </div>
);

export const IntegrationSummary: React.FC<IntegrationSummaryProps> = ({
  integration,
}) => {
  const { t } = useTranslation();
  const { config } = integration;

  if (config.kind === "EMAIL") {
    const rows: Row[] = [
      {
        label: t("communicationChannels.summary.email.from"),
        value: `${config.smtp.fromName} <${config.smtp.fromAddress}>`,
      },
      {
        label: t("communicationChannels.summary.email.replyTo"),
        value: config.smtp.replyToAddress,
      },
      {
        label: t("communicationChannels.summary.email.smtp"),
        value: `${config.smtp.host}:${config.smtp.port}${
          config.smtp.secure ? " (SSL)" : ""
        }`,
        mono: true,
      },
      {
        label: t("communicationChannels.summary.email.imap"),
        value: config.imap
          ? `${config.imap.host}:${config.imap.port} · ${config.imap.inboxFolder}`
          : t("communicationChannels.summary.email.imapOff"),
        mono: !!config.imap,
      },
    ];
    return <SummaryRows rows={rows} />;
  }

  if (config.kind === "TWILIO") {
    const rows: Row[] = [
      {
        label: t("communicationChannels.summary.twilio.smsPhoneNumber"),
        value:
          config.smsPhoneNumber ??
          t("communicationChannels.summary.twilio.smsPhoneNumberMissing"),
        mono: true,
      },
      {
        label: t("communicationChannels.summary.twilio.accountSid"),
        value: config.accountSid,
        mono: true,
      },
    ];
    return <SummaryRows rows={rows} />;
  }

  const rows: Row[] = [
    {
      label: t("communicationChannels.summary.whatsapp.displayPhoneNumber"),
      value: config.displayPhoneNumber,
      mono: true,
    },
    {
      label: t("communicationChannels.summary.whatsapp.wabaId"),
      value: config.wabaId,
      mono: true,
    },
    {
      label: t("communicationChannels.summary.whatsapp.phoneNumberId"),
      value: config.phoneNumberId,
      mono: true,
    },
    {
      label: t("communicationChannels.summary.whatsapp.graphApiVersion"),
      value: config.graphApiVersion,
      mono: true,
    },
  ];
  return <SummaryRows rows={rows} />;
};
